import type { Prettify } from '@/common/interfaces';
import { ENV } from '@/shared/config';

interface BaseQueryKeys<TPrefix extends string> {
  readonly all: readonly [TPrefix];
}

type CustomMethods = Record<string, (...args: unknown[]) => readonly unknown[]>;

type TransformCustomMethods<TPrefix extends string, TMethods extends CustomMethods> = {
  readonly [K in keyof TMethods]: TMethods[K] extends (...args: infer Args) => infer R
    ? R extends readonly unknown[]
      ? (...args: Args) => readonly [TPrefix, ...R]
      : never
    : never;
};

interface ListMethods<TPrefix extends string, TFilters extends Record<string, unknown>> {
  readonly lists: () => readonly [TPrefix, 'list'];
  readonly list: (filters: TFilters) => readonly [TPrefix, 'list', TFilters];
}

interface DetailsMethods<TPrefix extends string, TId extends string | number> {
  readonly details: () => readonly [TPrefix, 'detail'];
  readonly detail: (id: TId) => readonly [TPrefix, 'detail', TId];
}

class QueryKeysBuilder<TPrefix extends string, QueryKeys extends BaseQueryKeys<TPrefix>> {
  constructor(
    private prefix: TPrefix,
    private queryKeys: QueryKeys
  ) {}

  withList<TFilters extends Record<string, unknown>>(): QueryKeysBuilder<
    TPrefix,
    QueryKeys & ListMethods<TPrefix, TFilters>
  > {
    return new QueryKeysBuilder(this.prefix, {
      ...this.queryKeys,
      lists: () => [this.prefix, 'list'],
      list: (filters: TFilters) => [this.prefix, 'list', filters]
    });
  }

  withDetails<TId extends string | number>(): QueryKeysBuilder<TPrefix, QueryKeys & DetailsMethods<TPrefix, TId>> {
    return new QueryKeysBuilder(this.prefix, {
      ...this.queryKeys,
      details: () => [this.prefix, 'detail'],
      detail: (id: TId) => [this.prefix, 'detail', id]
    });
  }

  extend<TMethods extends Record<string, (...args: any[]) => readonly any[]>>(
    methods: TMethods
  ): QueryKeysBuilder<TPrefix, QueryKeys & TransformCustomMethods<TPrefix, TMethods>> {
    const customEntries = Object.entries(methods).map(([name, fn]) => {
      const wrapped = (...args: any[]) => {
        const tuple = (fn as (...args: any[]) => readonly any[])(...args);
        return [this.prefix, ...tuple] as const;
      };
      return [name, wrapped];
    });

    const customMethods = Object.fromEntries(customEntries) as TransformCustomMethods<TPrefix, TMethods>;

    return new QueryKeysBuilder(this.prefix, { ...this.queryKeys, ...customMethods });
  }

  value() {
    return Object.freeze(this.queryKeys) as Readonly<Prettify<QueryKeys>>;
  }
}

const usedPrefixes = new Set<string>();

function prefixGuard<TPrefix extends string>(prefix: TPrefix) {
  if (ENV.IS_DEV_MODE) {
    if (usedPrefixes.has(prefix)) {
      throw new Error(
        `DUPLICATE PREFIX: "${prefix}" is already used!\n` + `Choose a different prefix for this entity.`
      );
    }

    usedPrefixes.add(prefix);
  }

  return prefix;
}

export const createQueryKeys = <TPrefix extends string>(prefix: TPrefix) => {
  const safe = prefixGuard(prefix);
  return new QueryKeysBuilder<TPrefix, BaseQueryKeys<TPrefix>>(safe, { all: [safe] as const });
};
